import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Form, Button, Alert } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { selectAuthUserId } from '../redux/authSlice';
import {
  fetchUserReflectionQuestions,
  updateUserReflectionQuestions,
} from '../services/supabaseClient/profileService';
import { REFLECTION_QUESTIONS } from '../config/reflectionQuestions';
import FancyButton from '../components/ui/fancyButton';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const ReflectionQuestionsPreferences = () => {
  const userId = useSelector(selectAuthUserId);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const loadQuestions = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await fetchUserReflectionQuestions(userId);

    if (fetchError) {
      setError('Failed to load reflection questions');
      setQuestions(REFLECTION_QUESTIONS);
    } else if (data && data.length > 0) {
      setQuestions(data);
    } else {
      // Use default questions if none are saved
      setQuestions(REFLECTION_QUESTIONS);
    }

    setLoading(false);
  }, [userId]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const handleQuestionChange = (index, field, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      [field]: value,
    };
    setQuestions(updatedQuestions);
  };

  const handleAddQuestion = () => {
    setQuestions([
      ...questions,
      {
        id: `custom_${Date.now()}`,
        question: '',
        type: 'textarea',
        placeholder: '',
        required: false,
        rows: 2,
      },
    ]);
  };

  const handleRemoveQuestion = (index) => {
    const updatedQuestions = questions.filter((_, i) => i !== index);
    setQuestions(updatedQuestions);
  };

  const handleResetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset to default questions?')) {
      setQuestions(REFLECTION_QUESTIONS);
      setMessage('Questions reset to defaults. Click Save to apply changes.');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);

    const { error: saveError } = await updateUserReflectionQuestions(userId, questions);

    if (saveError) {
      setError('Failed to save reflection questions');
    } else {
      setMessage('Reflection questions saved successfully!');
      setTimeout(() => setMessage(null), 3000);
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <Container className="py-5">
        <LoadingSpinner />
      </Container>
    );
  }

  return (
    <Container className="py-5" style={{ maxWidth: '900px' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Reflection Questions</h2>
          <p className="text-muted">Customize the questions you see in your daily reflections</p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={handleResetToDefaults}>
            Reset to Defaults
          </Button>
          <FancyButton variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </FancyButton>
        </div>
      </div>

      {message && <Alert variant="success">{message}</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}

      <div className="mb-4">
        {questions.map((question, index) => (
          <Card key={question.id || index} className="mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <h5 className="mb-0">Question {index + 1}</h5>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleRemoveQuestion(index)}
                >
                  <i className="fa-solid fa-trash" />
                </Button>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Question Text</Form.Label>
                <Form.Control
                  type="text"
                  value={question.question}
                  onChange={(e) => handleQuestionChange(index, 'question', e.target.value)}
                  placeholder="Enter your question..."
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Placeholder Text (optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={question.placeholder || ''}
                  onChange={(e) => handleQuestionChange(index, 'placeholder', e.target.value)}
                  placeholder="Hint text for the answer..."
                />
              </Form.Group>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Question Type</Form.Label>
                    <Form.Select
                      value={question.type}
                      onChange={(e) => handleQuestionChange(index, 'type', e.target.value)}
                    >
                      <option value="textarea">Text Area</option>
                      <option value="slider">Rating Slider</option>
                    </Form.Select>
                  </Form.Group>
                </div>

                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Required Question"
                      checked={question.required || false}
                      onChange={(e) => handleQuestionChange(index, 'required', e.target.checked)}
                    />
                  </Form.Group>
                </div>
              </div>

              {question.type === 'textarea' && (
                <Form.Group className="mb-3">
                  <Form.Label>Number of Rows</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="10"
                    value={question.rows || 2}
                    onChange={(e) =>
                      handleQuestionChange(index, 'rows', parseInt(e.target.value, 10))
                    }
                  />
                </Form.Group>
              )}

              {question.type === 'slider' && (
                <div className="row">
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Min Value</Form.Label>
                      <Form.Control
                        type="number"
                        value={question.min || 1}
                        onChange={(e) =>
                          handleQuestionChange(index, 'min', parseInt(e.target.value, 10))
                        }
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group className="mb-3">
                      <Form.Label>Max Value</Form.Label>
                      <Form.Control
                        type="number"
                        value={question.max || 5}
                        onChange={(e) =>
                          handleQuestionChange(index, 'max', parseInt(e.target.value, 10))
                        }
                      />
                    </Form.Group>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        ))}
      </div>

      <div className="text-center">
        <Button variant="outline-primary" onClick={handleAddQuestion}>
          <i className="fa-solid fa-plus me-2" />
          Add New Question
        </Button>
      </div>
    </Container>
  );
};

export default ReflectionQuestionsPreferences;
